import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Button,
  Icon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Text,
  useColorModeValue,
  useToast,
  HStack,
  VStack,
  Badge,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Progress,
  Flex,
} from '@chakra-ui/react';
import { AddIcon } from '@chakra-ui/icons';

// Import components
import BasicInfoCard from './components/BasicInfoCard';
import RoleSelectionCard from './components/RoleSelectionCard';
import DepartmentSelectionCard from './components/DepartmentSelectionCard';
import SchemeSelectionCard from './components/SchemeSelectionCard';
import AdditionalFieldsCard from './components/AdditionalFieldsCard';

// Import hooks
import { useSelectStyles } from './hooks/useSelectStyles';
import { useAddUserForm } from './hooks/useAddUserForm';

// Import constants
import { roleConfig, schemeRoleMapping } from './constants/roleConfig';

const AddUserModal = ({
  isOpen,
  onClose,
  addUser,
  setAddUser,
  handleAddUser,
  loading,
}) => {
  const modalBg = useColorModeValue('white', 'gray.800');
  const modalTextColor = useColorModeValue('gray.800', 'white');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const footerBg = useColorModeValue('gray.50', 'gray.900');

  const toast = useToast();

  const [formProgress, setFormProgress] = useState(0);
  const [loginRole, setLoginRole] = useState('');
  const [userDepartment, setUserDepartment] = useState([]);
  const [step, setStep] = useState(1);
  const [userSchemes, setUserSchemes] = useState([]);

  // Use custom hooks
  const selectStyles = useSelectStyles();
  const {
    departments,
    schemes,
    talukas,
    fileTypesOptions,
    subDivisions,
    loadingDepartments,
    loadingSchemes,
    loadingFileTypes,
    loadingSubDivisions,
    loadingTalukas,
    availableDepartments,
    fetchSchemes,
    fetchFileTypes,
    fetchTalukas,
  } = useAddUserForm(isOpen, loginRole, userDepartment, userSchemes);

  useEffect(() => {
    let progress = 0;

    if (addUser.name && addUser.phoneNo && addUser.userId) progress += 25;

    if (addUser.role) progress += 25;

    if (addUser.department && addUser.department.length > 0) {
      const config = roleConfig[addUser.role];
      if (config) {
        if (
          config.departmentType === 'multiple' &&
          addUser.department.length >= 1
        ) {
          progress += 25;
        } else if (
          config.departmentType === 'single' &&
          addUser.department.length === 1
        ) {
          progress += 25;
        }
      }
    }

    if (addUser.schemes && addUser.schemes.length > 0) progress += 25;

    setFormProgress(Math.min(progress, 100));

    if (!addUser.role) setStep(1);
    else if (!addUser.department || addUser.department.length === 0) setStep(2);
    else if (!addUser.schemes || addUser.schemes.length === 0) setStep(3);
    else setStep(4);
  }, [addUser]);

  useEffect(() => {
    if (isOpen) {
      const userData = localStorage.getItem('user');
      if (userData) {
        const parsed = JSON.parse(userData);
        setLoginRole(parsed.role);
        setUserDepartment(parsed.department || []);
        setUserSchemes(parsed.schemes || []);
      }
    }
  }, [isOpen]);

  const getAllowedRolesForCreator = (creatorRole) => {
    if (!creatorRole) return [];

    if (creatorRole === 'schemeAdmin') {
      return Object.keys(roleConfig).filter(
        (role) =>
          roleConfig[role].canBeCreatedBy?.includes('schemeAdmin') &&
          !['departmentOfficer', 'admin', 'schemeAdmin'].includes(role),
      );
    }

    return Object.keys(roleConfig).filter((role) =>
      roleConfig[role].canBeCreatedBy?.includes(creatorRole),
    );
  };

  const handleRoleChange = (role) => {
    setAddUser((prev) => ({
      ...prev,
      role,
      department: [],
      schemes: [],
      fileTypes: [],
      tahsils: [],
      subDivision: '',
    }));
  };

  const handleDepartmentChange = (selectedOptions) => {
    const departmentIds = selectedOptions
      ? Array.isArray(selectedOptions)
        ? selectedOptions.map((opt) => opt.value)
        : [selectedOptions.value]
      : [];

    setAddUser((prev) => ({
      ...prev,
      department: departmentIds,
      schemes: [],
      fileTypes: [],
    }));
  };

  const handleSchemeChange = (selectedOptions) => {
    const selectedValues = selectedOptions
      ? selectedOptions.map((opt) => opt.value)
      : [];

    setAddUser((prev) => ({
      ...prev,
      schemes: selectedValues,
      fileTypes: [],
    }));
  };

  useEffect(() => {
    if (addUser.role && addUser.department && addUser.department.length > 0) {
      fetchSchemes(addUser, userSchemes, loginRole);
    }
  }, [addUser.department, addUser.role, fetchSchemes, userSchemes, loginRole]);

  useEffect(() => {
    fetchFileTypes(addUser);
  }, [addUser, fetchFileTypes]);

  useEffect(() => {
    fetchTalukas(addUser.role);
  }, [addUser.role, fetchTalukas]);

  const mappedAvailableDepartments = availableDepartments.map((d) => ({
    value: d._id,
    label: d.name,
  }));

  const mappedSchemes = schemes.map((item) => ({
    value: item._id,
    label: item.name,
    color: schemeRoleMapping[item._id]?.color || 'blue',
  }));

  const handleClose = () => {
    setAddUser({
      name: '',
      phoneNo: '',
      role: '',
      department: [],
      userId: '',
      schemes: [],
      tahsils: [],
      fileTypes: [],
      subDivision: '',
      nameOfOffice: '',
      designation: '',
    });
    onClose();
  };

  const isFormValid = () => {
    const {
      name,
      phoneNo,
      role,
      department,
      userId,
      schemes,
      nameOfOffice,
      designation,
    } = addUser;
    const roleCfg = roleConfig[role];

    if (!name || !phoneNo || !role || !userId || !nameOfOffice || !designation)
      return false;
    if (!department || department.length === 0) return false;

    if (roleCfg?.departmentType === 'single' && department.length !== 1)
      return false;
    if (roleCfg?.requiresSchemes && (!schemes || schemes.length === 0))
      return false;
    if (role === 'subDivisionalOfficer' && !addUser.subDivision) return false;
    if (
      role === 'talukaUser' &&
      (!addUser.tahsils || addUser.tahsils.length === 0)
    )
      return false;
    if (
      ['projectOfficer', 'subDivisionalOfficer'].includes(role) &&
      (!addUser.fileTypes || addUser.fileTypes.length === 0)
    )
      return false;

    return true;
  };

  const allowedRoles = getAllowedRolesForCreator(loginRole);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="4xl"
      scrollBehavior="outside"
    >
      <ModalOverlay backdropFilter="blur(4px)" />
      <ModalContent
        rounded="xl"
        shadow="2xl"
        overflow="hidden"
        bg={modalBg}
        maxH="90vh"
      >
        <Box bgGradient="linear(to-r, blue.600, purple.600)" py={2} px={6}>
          <ModalHeader
            fontSize="xl"
            fontWeight="bold"
            color="white"
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            p={0}
          >
            <HStack spacing={3}>
              <Icon as={AddIcon} boxSize={5} />
              <VStack align="start" spacing={0}>
                <Text fontSize="lg">Add New User</Text>
                <Text fontSize="xs" opacity={0.9}>
                  Step {step} of 4 • {formProgress}% Complete
                </Text>
              </VStack>
            </HStack>
            <ModalCloseButton
              color="white"
              position="relative"
              top={0}
              right={0}
              _hover={{ bg: 'rgba(255,255,255,0.2)' }}
            />
          </ModalHeader>
        </Box>

        <ModalBody bg={modalBg} px={6} py={0} overflowY="auto">
          <BasicInfoCard addUser={addUser} setAddUser={setAddUser} />

          {allowedRoles.length === 0 && loginRole ? (
            <Alert status="warning" mb={6}>
              <AlertIcon />
              You don't have permission to create users.
            </Alert>
          ) : (
            <>
              <RoleSelectionCard
                addUser={addUser}
                handleRoleChange={handleRoleChange}
                allowedRoles={allowedRoles}
                roleConfig={roleConfig}
              />

              <DepartmentSelectionCard
                addUser={addUser}
                roleConfig={roleConfig}
                loadingDepartments={loadingDepartments}
                mappedAvailableDepartments={mappedAvailableDepartments}
                departments={departments}
                selectStyles={selectStyles}
                handleDepartmentChange={handleDepartmentChange}
                loginRole={loginRole}
              />

              <SchemeSelectionCard
                addUser={addUser}
                roleConfig={roleConfig}
                loadingSchemes={loadingSchemes}
                schemes={schemes}
                mappedSchemes={mappedSchemes}
                selectStyles={selectStyles}
                handleSchemeChange={handleSchemeChange}
                schemeRoleMapping={schemeRoleMapping}
              />

              <AdditionalFieldsCard
                addUser={addUser}
                setAddUser={setAddUser}
                subDivisions={subDivisions}
                selectStyles={selectStyles}
                loadingFileTypes={loadingFileTypes}
                fileTypesOptions={fileTypesOptions}
                talukas={talukas}
                loadingTalukas={loadingTalukas}
                role={addUser.role}
              />
            </>
          )}

          {addUser.role && (
            <Alert
              status="info"
              variant="subtle"
              borderRadius="lg"
              fontSize="sm"
              mb={6}
            >
              <AlertIcon />
              <Box>
                <AlertTitle fontSize="sm">Configuration Summary</AlertTitle>
                <Text fontSize="xs">
                  Creating{' '}
                  <Badge colorScheme={roleConfig[addUser.role]?.color}>
                    {roleConfig[addUser.role]?.label}
                  </Badge>
                  {addUser.department.length > 0 &&
                    ` for ${addUser.department.length} department(s)`}
                  {addUser.schemes.length > 0 &&
                    ` with ${addUser.schemes.length} scheme(s)`}
                  {loginRole === 'schemeAdmin' && ' (created by Scheme Admin)'}
                </Text>
              </Box>
            </Alert>
          )}
        </ModalBody>

        <ModalFooter
          bg={footerBg}
          borderTop="1px solid"
          borderColor={borderColor}
          px={6}
          py={4}
        >
          <HStack spacing={3} w="full" justify="space-between">
            <Button onClick={handleClose} variant="outline" colorScheme="gray">
              Cancel
            </Button>

            <Button
              colorScheme="blue"
              onClick={handleAddUser}
              isLoading={loading}
              loadingText="Creating..."
              leftIcon={<AddIcon />}
              isDisabled={!isFormValid()}
            >
              Create User
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AddUserModal;
